<template>
  <div class="wuxia-notice">
    <header class="topbar">
      <div class="shell topbar__inner">
        <div class="brand">
          <svg class="brand__mark" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <path d="M20 3 24 16 37 20 24 24 20 37 16 24 3 20 16 16Z" stroke="currentColor" stroke-width="1" opacity="0.85"/>
            <circle cx="20" cy="20" r="5.2" stroke="currentColor" stroke-width="1" opacity="0.55"/>
            <circle cx="20" cy="20" r="1.6" fill="currentColor"/>
          </svg>
          <span class="brand__name">舞侠<em>ONLINE</em></span>
        </div>
        <div class="topbar__tag">
          <span class="topbar__dot"></span>
          <b>官方公告</b>
          <span>OFFICIAL NOTICE</span>
        </div>
      </div>
    </header>

    <main>
      <section class="hero">
        <div class="hero__bg" aria-hidden="true"></div>
        <div class="hero__veil" aria-hidden="true"></div>
        <div class="hero__frame" aria-hidden="true"></div>

        <div class="shell hero__inner">
          <div class="hero__content">
            <div class="eyebrow">
              <span class="eyebrow__rule"></span>
              重塑版本 · 开放测试
              <span class="eyebrow__en">Remastered Open Beta</span>
            </div>

            <h1>舞侠<span class="latin">Online</span><br><em>重塑版</em>开放测试公告</h1>

            <div class="hero__meta">
              <span>发布时间 <b>{{ publishDateText }}</b></span>
              <span class="hero__meta-sep"></span>
              <span>开测日期 <b>{{ openDateText }}</b></span>
              <span class="hero__meta-sep"></span>
              <span class="hero__count">
                <template v-if="phase === 'pending'">距开测 <b>{{ daysToStart }}</b> 天</template>
                <template v-else-if="phase === 'open'">距开测 <b class="hero__count--flag">OPEN</b> 测试进行中</template>
                <template v-else>距开测 <b class="hero__count--flag">CLOSED</b> 测试已结束</template>
              </span>
            </div>
          </div>
        </div>

        <div class="seal" aria-hidden="true">
          <div class="seal__grid">
            <span v-for="(ch, i) in sealChars" :key="i">{{ ch }}</span>
          </div>
        </div>
      </section>

      <section class="lead">
        <div class="shell">
          <div class="lead__panel">
            <div class="lead__mark" aria-hidden="true">
              <span>序</span>
              <i></i>
            </div>
            <p class="lead__text">
              诸位侠士，久候了。历经长时间底层重构，<b>舞侠Online 重塑版</b>已完成阶段性打磨，将于
              <b>{{ openDateText }}</b> 开启开放测试。本次测试覆盖多分辨率适配、窗口模式修复、程序层优化与 UI 高清化等核心改造，
              同时准备了三项面向全体玩家的活动。原正式服账号即可直接登录测试服，无需报名。诚邀诸位重返江湖，共验新篇。
            </p>
          </div>
        </div>
      </section>

      <section class="section" id="tech">
        <div class="shell">
          <div class="section-head reveal" v-reveal>
            <div class="section-head__num">壹</div>
            <div class="section-head__title">
              <h2>技术升级</h2>
              <span>Technical Upgrade</span>
            </div>
            <p class="section-head__note">重塑版对底层程序与界面资源进行了系统性重构，本次测试将集中验证以下改动。</p>
          </div>

          <div class="tech">
            <article class="tech__item reveal" v-reveal>
              <div class="tech__top">
                <span class="tech__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="square">
                    <rect x="2.5" y="4.5" width="19" height="13" rx="1"/>
                    <path d="M8 21h8M12 17.5V21"/>
                    <path d="M7 8.5h6M7 12h3"/>
                  </svg>
                </span>
                <span class="tech__idx">01</span>
              </div>
              <h3>支持多分辨率选择</h3>
              <p>新增分辨率自由选择，全屏与窗口两种显示方式均可适配不同尺寸显示器，画面比例不再被拉伸。</p>
            </article>

            <article class="tech__item reveal" v-reveal>
              <div class="tech__top">
                <span class="tech__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="square">
                    <rect x="3" y="4" width="13" height="10" rx="1"/>
                    <rect x="9" y="10" width="12" height="10" rx="1"/>
                  </svg>
                </span>
                <span class="tech__idx">02</span>
              </div>
              <h3>大窗口与窗口模式优化修复</h3>
              <p>针对大窗口模式与窗口模式的缩放、切换与显示异常逐项修复，多开与切屏体验更加稳定。</p>
            </article>

            <article class="tech__item reveal" v-reveal>
              <div class="tech__top">
                <span class="tech__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="square">
                    <path d="M12 2.8 21 7.6v8.8L12 21.2 3 16.4V7.6Z"/>
                    <path d="M12 12 21 7.6M12 12v9.2M12 12 3 7.6"/>
                  </svg>
                </span>
                <span class="tech__idx">03</span>
              </div>
              <h3>程序层设计全面优化</h3>
              <p>几乎所有程序层设计完成优化升级，帧率更稳、加载更快、长时间运行更流畅，游戏稳定性显著提升。</p>
            </article>

            <article class="tech__item reveal" v-reveal>
              <div class="tech__top">
                <span class="tech__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="square">
                    <rect x="3" y="3" width="18" height="18" rx="1"/>
                    <path d="M3 9h18M9 9v12"/>
                    <path d="M13 13.5h4M13 17h2.5"/>
                  </svg>
                </span>
                <span class="tech__idx">04</span>
              </div>
              <h3>UI 高清化处理持续推进</h3>
              <p>界面与美术资源按批次高清重制，本次测试已覆盖主要界面，后续版本将持续推进至全部模块。</p>
            </article>

            <article class="tech__item tech__item--new reveal" v-reveal>
              <div class="tech__top">
                <span class="tech__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="square">
                    <path d="M12 3.2 14.4 9l6.3.5-4.8 4.1 1.5 6.2L12 16.5 6.6 19.8l1.5-6.2L3.3 9.5 9.6 9Z"/>
                  </svg>
                </span>
                <span class="tech__idx">05</span>
              </div>
              <h3>更多神秘新内容</h3>
              <p>新的玩法与内容正在紧锣密鼓地准备中，将在后续版本中陆续揭开面纱，敬请期待。</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section" id="schedule">
        <div class="shell">
          <div class="section-head reveal" v-reveal>
            <div class="section-head__num">贰</div>
            <div class="section-head__title">
              <h2>测试安排</h2>
              <span>Test Schedule</span>
            </div>
            <p class="section-head__note">测试服与原正式服账号互通，无需报名，按公布时段登录即可参与。</p>
          </div>

          <div class="testpanel reveal" v-reveal>
            <div class="testpanel__col testpanel__col--main">
              <span class="field-label">测试时间</span>
              <div class="date-line">
                <span>{{ startMD }}</span><i>—</i><span>{{ endMD }}</span><small>{{ startYear }}</small>
              </div>
              <div class="date-year">{{ dailyOpenText }}</div>
            </div>

            <div class="testpanel__col">
              <span class="field-label">参与方式</span>
              <div class="time-big">原账号<em>直接登录</em></div>
              <p class="col-note"><strong>无需报名。</strong>使用原正式服账号即可登录测试服，无需额外申请测试资格。</p>
            </div>

            <div class="testpanel__col testpanel__col--download">
              <span class="field-label">客户端下载</span>
              <div class="dl-date">{{ clientDownloadDate }}</div>
              <p class="col-note">专用测试客户端已于 <strong>{{ clientDownloadShort }}</strong> 在官方下载中心开放下载，请提前完成安装。</p>
            </div>
          </div>

          <div class="notice-strip reveal" v-reveal>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 7.5v5.5l3.5 2"/>
            </svg>
            <span>测试服与正式服为<b>两个独立客户端</b>，请通过官方下载中心获取测试专用版本，以免登录失败。</span>
          </div>
        </div>
      </section>

      <section class="section" id="events">
        <div class="shell">
          <div class="section-head reveal" v-reveal>
            <div class="section-head__num">叁</div>
            <div class="section-head__title">
              <h2>活动专区</h2>
              <span>In-Game Events</span>
            </div>
            <p class="section-head__note">测试期间同步开启三项活动，奖励覆盖测试服与正式服，欢迎诸位侠士逐一体验。</p>
          </div>

          <div class="acts">
            <article class="act reveal" v-reveal>
              <span class="act__ghost" aria-hidden="true">A</span>
              <div class="act__head">
                <span class="act__tag">甲</span>
                <span class="act__kind">Event A · 反馈有礼</span>
              </div>
              <h3>我的游戏我要纠错</h3>
              <p class="act__desc">
                测试期间发现问题，按要求发送邮件至指定邮箱：邮件标题需符合规定格式，并附上
                <strong>log 压缩包</strong> 与 <strong>问题截图</strong>。经核实有效的问题反馈，即可获得奖励。
              </p>
              <div class="act__reward">
                <div class="act__reward-label">奖励</div>
                <p><em>GM 钥匙礼包</em> · 内含幸运钥匙 ×30</p>
              </div>
            </article>

            <article class="act reveal" v-reveal>
              <span class="act__ghost" aria-hidden="true">B</span>
              <div class="act__head">
                <span class="act__tag">乙</span>
                <span class="act__kind">Event B · 时长回馈</span>
              </div>
              <h3>泡点不许断</h3>
              <p class="act__desc">
                测试期间，每日测试时段内的游戏时长将被统计并转换为泡点。所获泡点
                <strong>累计计入正式服</strong>，测试结束后统一结算，不因测试结束而清零。
              </p>
              <div class="act__reward">
                <div class="act__reward-label">奖励</div>
                <p>测试时长换算<em>泡点</em>，累计计入正式服</p>
              </div>
            </article>

            <article class="act reveal" v-reveal>
              <span class="act__ghost" aria-hidden="true">C</span>
              <div class="act__head">
                <span class="act__tag">丙</span>
                <span class="act__kind">Event C · 排行榜</span>
              </div>
              <h3>战力大比拼</h3>
              <p class="act__desc">
                排行榜将统计测试期间的游戏总分，按最终排名发放奖励。榜单前列还可额外获得专属荣誉与定制内容。
              </p>
              <div class="act__reward">
                <div class="act__reward-label">奖励</div>
                <ul class="ranks">
                  <li v-for="rank in rankRewards" :key="rank.label">
                    <b>{{ rank.label }}</b><span>{{ rank.reward }}</span>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="section section--tips">
        <div class="shell">
          <div class="divider" aria-hidden="true">
            <span class="divider__line"></span>
            <span class="divider__mark"></span>
            <span class="divider__line"></span>
          </div>

          <div class="tips reveal" v-reveal>
            <div class="tips__label">
              <h3>温馨提示</h3>
              <span>Before You Log In</span>
            </div>
            <ul class="tips__list">
              <li v-for="(tip, i) in tips" :key="i">{{ tip }}</li>
            </ul>
          </div>

          <div class="signoff reveal" v-reveal>
            <div class="signoff__text">
              <p class="signoff__team">舞侠Online 运营团队</p>
              <p class="signoff__date">{{ publishDateSpacedText }}</p>
            </div>
            <div class="signoff__seal" aria-hidden="true">舞侠<br>运营</div>
          </div>
        </div>
      </section>
    </main>

    <footer class="pagefoot">
      <div class="shell pagefoot__inner">
        <span>© {{ startYear }} 舞侠Online <em>·</em> 重塑版开放测试</span>
        <span>本公告内容以官方后续说明为准</span>
      </div>
    </footer>
  </div>
</template>

<script>
/* 开测起止时间：与原 script.js 一致（月份从 0 开始） */
const TEST_START = new Date(2026, 9, 28, 14, 0, 0);
const TEST_END = new Date(2026, 10, 11, 16, 0, 0);
const ONE_DAY = 86400000;

function pad(n) {
  return n < 10 ? "0" + n : String(n);
}

export default {
  name: "WuxiaBetaNotice",

  /* v-reveal：替代原 IntersectionObserver 滚动揭示逻辑 */
  directives: {
    reveal: {
      inserted(el, binding, vnode) {
        const vm = vnode.context;
        if (!vm || !vm.$_revealEngine) {
          el.classList.add("is-in");
          return;
        }
        const engine = vm.$_revealEngine;
        if (engine.disabled) {
          el.classList.add("is-in");
          return;
        }
        /* 同一父级下的兄弟元素依次错开进场 */
        const siblings = el.parentElement ? el.parentElement.children : [];
        const index = Array.prototype.indexOf.call(siblings, el);
        if (index > 0) {
          el.style.transitionDelay = Math.min(index * 90, 360) + "ms";
        }
        engine.observe(el);
      },
      unbind(el, binding, vnode) {
        const vm = vnode.context;
        if (vm && vm.$_revealEngine && !vm.$_revealEngine.disabled) {
          vm.$_revealEngine.observer.unobserve(el);
        }
      }
    }
  },

  data() {
    return {
      now: Date.now(),
      publishDateText: "2026年10月23日",
      publishDateSpacedText: "2026 年 10 月 23 日",
      openDateText: "2026年10月28日",
      dailyOpenText: "每日 14:00 — 16:00 开放",
      clientDownloadDate: "2026.10.22",
      clientDownloadShort: "10月22日",
      sealChars: ["重", "铸", "江", "湖"],
      tips: [
        "使用原正式服账号直接登录测试服，无需报名或另行申请资格。",
        "每日测试时段为 14:00 — 16:00，请留意时间安排，避免错过统计。",
        "纠错活动请按规定格式发送邮件标题，并附 log 压缩包与截图，便于核对发放奖励。",
        "测试客户端请于官方下载中心获取，10 月 22 日起已开放下载。"
      ],
      rankRewards: [
        { label: "榜首", reward: "官方专属定制曲目一首（风格 / BPM / 歌手名自选）" },
        { label: "前三", reward: "【舞侠核心骨干】称号" },
        { label: "前十", reward: "月卡一张" }
      ]
    };
  },

  computed: {
    phase() {
      if (this.now < TEST_START.getTime()) return "pending";
      if (this.now <= TEST_END.getTime()) return "open";
      return "ended";
    },
    daysToStart() {
      return Math.ceil((TEST_START.getTime() - this.now) / ONE_DAY);
    },
    startMD() {
      return pad(TEST_START.getMonth() + 1) + "." + pad(TEST_START.getDate());
    },
    endMD() {
      return pad(TEST_END.getMonth() + 1) + "." + pad(TEST_END.getDate());
    },
    startYear() {
      return String(TEST_START.getFullYear());
    }
  },

  created() {
    /* 观察者只建一次，供 v-reveal 指令共享；非响应式属性 */
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce || typeof IntersectionObserver === "undefined") {
      this.$_revealEngine = { disabled: true, observer: null, observe() {} };
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-in");
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
      );
      this.$_revealEngine = {
        disabled: false,
        observer,
        observe: (el) => observer.observe(el)
      };
    }
  },

  mounted() {
    /* 倒计时按分钟刷新，跨天时状态自动流转 */
    this.$_timer = setInterval(() => {
      this.now = Date.now();
    }, 60000);
  },

  beforeDestroy() {
    if (this.$_timer) {
      clearInterval(this.$_timer);
      this.$_timer = null;
    }
    if (this.$_revealEngine && this.$_revealEngine.observer) {
      this.$_revealEngine.observer.disconnect();
    }
    this.$_revealEngine = null;
  }
};
</script>

<style scoped>
@import url("https://miaoda.feishu.cn/fonts/css2?family=Noto+Serif+SC:wght@400;600;700&family=Noto+Sans+SC:wght@300;400;500&family=LXGW+WenKai+TC:wght@400;700&display=swap");

/* ============================================================
   舞侠Online · 重塑版开放测试公告
   Palette: ink stone + gold leaf + cinnabar seal
   原 styles.css 的 :root / body 变量与底色收敛到组件根节点，
   避免组件污染全局 html / body。
   ============================================================ */

.wuxia-notice {
  --ink-950: #06080a;
  --ink-900: #090b0e;
  --ink-850: #0c0f13;
  --ink-800: #11151a;
  --ink-750: #161b21;
  --ink-700: #1d232b;

  --gold: #c9a961;
  --gold-soft: #e3cd9b;
  --gold-deep: #8a6c37;
  --cinnabar: #a8402f;
  --cinnabar-soft: #c96a52;
  --jade: #7fa296;

  --text: #e8e2d6;
  --text-dim: #9c968a;
  --text-faint: #6f6a62;

  --line: rgba(201, 169, 97, 0.22);
  --line-soft: rgba(201, 169, 97, 0.12);

  --shell: 1180px;
  --serif: "Noto Serif SC", "Songti SC", "SimSun", serif;
  --kai: "LXGW WenKai TC", "Kaiti SC", "STKaiti", var(--serif);
  --sans: "Noto Sans SC", -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif;

  position: relative;
  min-height: 100vh;
  background: var(--ink-900);
  color: var(--text);
  font-family: var(--sans);
  font-weight: 300;
  font-size: 16px;
  line-height: 1.85;
  letter-spacing: 0.02em;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

.wuxia-notice,
.wuxia-notice *,
.wuxia-notice *::before,
.wuxia-notice *::after {
  box-sizing: border-box;
}

.wuxia-notice::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.55;
  background-image:
    radial-gradient(1100px 620px at 78% -8%, rgba(201, 169, 97, 0.09), transparent 62%),
    radial-gradient(900px 700px at 6% 22%, rgba(96, 122, 118, 0.06), transparent 60%),
    radial-gradient(1200px 900px at 50% 108%, rgba(168, 64, 47, 0.07), transparent 65%);
}

.wuxia-notice::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.035;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");
}

.wuxia-notice a {
  color: var(--gold-soft);
  text-decoration: none;
}

.wuxia-notice ::selection {
  background: rgba(201, 169, 97, 0.28);
  color: #fff;
}

.shell {
  width: 100%;
  max-width: var(--shell);
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 2;
}

/* ---------- top bar ---------- */

.topbar {
  position: relative;
  z-index: 5;
  border-bottom: 1px solid rgba(201, 169, 97, 0.14);
  background: rgba(6, 8, 10, 0.72);
  backdrop-filter: blur(14px);
}

.topbar__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  height: 68px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.brand__mark {
  width: 34px;
  height: 34px;
  flex: none;
  color: var(--gold);
}

.brand__name {
  font-family: var(--serif);
  font-size: 19px;
  font-weight: 600;
  letter-spacing: 0.16em;
  color: var(--text);
  white-space: nowrap;
}

.brand__name em {
  font-style: normal;
  font-family: var(--serif);
  font-size: 12px;
  letter-spacing: 0.34em;
  color: var(--gold);
  margin-left: 8px;
  vertical-align: 2px;
}

.topbar__tag {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11px;
  letter-spacing: 0.3em;
  color: var(--text-faint);
  white-space: nowrap;
}

.topbar__tag b {
  font-weight: 400;
  color: var(--gold-soft);
  letter-spacing: 0.24em;
}

.topbar__dot {
  width: 3px;
  height: 3px;
  background: var(--gold-deep);
  transform: rotate(45deg);
}

/* ---------- hero ---------- */

.hero {
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid var(--line-soft);
}

.hero__bg {
  position: absolute;
  inset: 0;
  /* 原静态站托管地址，落地到本地项目时建议换成 ./assets/hero.jpg 等资源引用 */
  /* background-image: url("/spark/app/app_17gkxpxjqkg/runtime/api/v1/storage/object/bucket_aadk3lp53j6au_static/static%2Faadk3lm37a6eu_ve_miaoda"); */
  background-size: cover;
  background-position: center 58%;
  filter: grayscale(0.35) brightness(0.62) contrast(1.06);
  transform: scale(1.02);
}

.hero__veil {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(6, 8, 10, 0.92) 0%, rgba(6, 8, 10, 0.66) 46%, rgba(6, 8, 10, 0.18) 100%),
    linear-gradient(180deg, rgba(6, 8, 10, 0.55) 0%, rgba(6, 8, 10, 0) 34%, rgba(9, 11, 14, 0.86) 88%, var(--ink-900) 100%);
}

.hero__inner {
  position: relative;
  z-index: 2;
  padding-top: 96px;
  padding-bottom: 104px;
}

.hero__frame {
  position: absolute;
  inset: 26px max(22px, calc((100% - 1260px) / 2));
  border: 1px solid rgba(201, 169, 97, 0.16);
  pointer-events: none;
  z-index: 1;
}

.hero__frame::before,
.hero__frame::after {
  content: "";
  position: absolute;
  width: 9px;
  height: 9px;
  border: 1px solid var(--gold);
  opacity: 0.55;
}

.hero__frame::before {
  top: -5px;
  left: -5px;
}

.hero__frame::after {
  bottom: -5px;
  right: -5px;
}

.hero__content {
  position: relative;
  max-width: 800px;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 11px;
  letter-spacing: 0.42em;
  color: var(--gold);
  text-transform: uppercase;
}

.eyebrow__rule {
  width: 46px;
  height: 1px;
  background: linear-gradient(90deg, var(--gold), rgba(201, 169, 97, 0.1));
}

.eyebrow__en {
  color: var(--text-faint);
  letter-spacing: 0.3em;
  font-size: 10px;
}

.hero h1 {
  margin: 34px 0 0;
  font-family: var(--serif);
  font-weight: 600;
  font-size: clamp(38px, 6vw, 78px);
  line-height: 1.22;
  letter-spacing: 0.035em;
  color: #f2ece0;
  text-wrap: pretty;
}

.hero h1 .latin {
  font-family: var(--serif);
  font-size: 0.62em;
  letter-spacing: 0.14em;
  color: var(--gold-soft);
  font-weight: 400;
  margin-left: 0.12em;
}

.hero h1 em {
  font-style: normal;
  color: var(--gold);
  position: relative;
}

.hero h1 em::after {
  content: "";
  position: absolute;
  left: 0;
  right: -0.08em;
  bottom: 0.1em;
  height: 1px;
  background: linear-gradient(90deg, rgba(201, 169, 97, 0.7), rgba(201, 169, 97, 0));
}

.hero__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 28px;
  margin-top: 38px;
  font-size: 12px;
  letter-spacing: 0.18em;
  color: var(--text-dim);
}

.hero__meta b {
  font-weight: 400;
  color: var(--text);
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
}

.hero__meta-sep {
  width: 1px;
  height: 12px;
  background: var(--line);
}

.hero__count {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 16px 8px 14px;
  border: 1px solid rgba(168, 64, 47, 0.5);
  background: rgba(168, 64, 47, 0.1);
  color: var(--cinnabar-soft);
  font-size: 11px;
  letter-spacing: 0.22em;
}

.hero__count b {
  font-family: var(--serif);
  font-size: 17px;
  color: #f0d9c8;
  letter-spacing: 0.06em;
}

/* OPEN / CLOSED 状态（替代原脚本里的内联样式） */
.hero__count--flag {
  font-size: 13px;
  letter-spacing: 0.16em;
}

/* seal */

.seal {
  position: absolute;
  right: 34px;
  bottom: 44px;
  z-index: 3;
  width: 92px;
  height: 92px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: linear-gradient(160deg, #b24a37, #8e3325);
  box-shadow: 0 10px 34px rgba(140, 44, 30, 0.34), inset 0 0 0 1px rgba(255, 228, 210, 0.24);
  transform: rotate(-3deg);
  opacity: 0.94;
}

.seal__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  width: 62px;
  height: 62px;
  padding: 7px;
  border: 1px solid rgba(255, 232, 218, 0.55);
  font-family: var(--serif);
  font-weight: 600;
  font-size: 21px;
  line-height: 1;
  color: #fceee6;
  text-align: center;
}

.seal__grid span {
  display: grid;
  place-items: center;
}

/* ---------- lead ---------- */

.lead {
  position: relative;
  z-index: 3;
  margin-top: -54px;
  padding-bottom: 8px;
}

.lead__panel {
  position: relative;
  display: grid;
  grid-template-columns: 74px 1fr;
  gap: 30px;
  padding: 46px 52px 46px 30px;
  background: linear-gradient(150deg, rgba(23, 28, 34, 0.94), rgba(12, 15, 19, 0.94));
  border: 1px solid var(--line);
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.42);
}

.lead__panel::before {
  content: "";
  position: absolute;
  left: -1px;
  top: 26px;
  bottom: 26px;
  width: 2px;
  background: linear-gradient(180deg, transparent, var(--gold), transparent);
}

.lead__mark {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-family: var(--serif);
  color: var(--gold);
}

.lead__mark span {
  font-size: 34px;
  line-height: 1;
}

.lead__mark i {
  width: 1px;
  flex: 1;
  min-height: 46px;
  background: linear-gradient(180deg, var(--line), transparent);
}

.lead__text {
  font-family: var(--kai);
  font-size: 18px;
  line-height: 2.05;
  color: #d8d2c6;
  margin: 0;
  text-wrap: pretty;
}

.lead__text b {
  font-weight: 700;
  color: var(--gold-soft);
}

/* ---------- section base ---------- */

.section {
  position: relative;
  padding: 92px 0 0;
}

.section-head {
  display: grid;
  grid-template-columns: 96px 1fr auto;
  align-items: end;
  gap: 26px;
  padding-bottom: 26px;
  border-bottom: 1px solid var(--line);
}

.section-head__num {
  font-family: var(--serif);
  font-size: 54px;
  line-height: 1;
  color: rgba(201, 169, 97, 0.07);
  -webkit-text-stroke: 1px rgba(201, 169, 97, 0.5);
  letter-spacing: 0.04em;
}

.section-head__title {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.section-head__title h2 {
  margin: 0;
  font-family: var(--serif);
  font-size: clamp(24px, 2.6vw, 31px);
  font-weight: 600;
  letter-spacing: 0.14em;
  color: #f1ebdf;
}

.section-head__title span {
  font-size: 10px;
  letter-spacing: 0.36em;
  color: var(--text-faint);
  text-transform: uppercase;
}

.section-head__note {
  max-width: 320px;
  margin: 0;
  font-size: 13px;
  line-height: 1.9;
  color: var(--text-dim);
  text-align: right;
}

/* ---------- tech grid ---------- */

.tech {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 2px;
  border-left: 1px solid var(--line-soft);
}

.tech__item {
  position: relative;
  padding: 36px 32px 34px;
  border-right: 1px solid var(--line-soft);
  border-bottom: 1px solid var(--line-soft);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 60%);
  transition: background 0.45s ease;
}

.tech__item:hover {
  background: linear-gradient(180deg, rgba(201, 169, 97, 0.07), transparent 70%);
}

.tech__item--wide {
  grid-column: span 2;
}

.tech__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 20px;
}

.tech__icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  color: var(--gold);
  background: rgba(201, 169, 97, 0.05);
}

.tech__icon svg {
  width: 21px;
  height: 21px;
}

.tech__idx {
  font-family: var(--serif);
  font-size: 12px;
  letter-spacing: 0.2em;
  color: var(--text-faint);
  font-variant-numeric: tabular-nums;
}

.tech__item h3 {
  margin: 0 0 10px;
  font-family: var(--serif);
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: #efe9dd;
}

.tech__item p {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.95;
  color: var(--text-dim);
}

.tech__item--new h3::after {
  content: "筹备中";
  display: inline-block;
  margin-left: 10px;
  padding: 1px 7px;
  font-family: var(--sans);
  font-size: 10px;
  letter-spacing: 0.14em;
  color: var(--cinnabar-soft);
  border: 1px solid rgba(201, 106, 82, 0.4);
  vertical-align: 3px;
  font-weight: 400;
}

/* ---------- test arrangement ---------- */

.testpanel {
  display: grid;
  grid-template-columns: 1.35fr 1fr 1fr;
  margin-top: 42px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(20, 25, 31, 0.86), rgba(11, 14, 18, 0.9));
  position: relative;
}

.testpanel::before,
.testpanel::after {
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  border: 1px solid var(--gold);
  opacity: 0.4;
}

.testpanel::before {
  top: 10px;
  left: 10px;
  border-right: 0;
  border-bottom: 0;
}

.testpanel::after {
  bottom: 10px;
  right: 10px;
  border-left: 0;
  border-top: 0;
}

.testpanel__col {
  padding: 40px 38px 38px;
  border-right: 1px solid var(--line-soft);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.testpanel__col:last-child {
  border-right: 0;
}

.field-label {
  font-size: 10px;
  letter-spacing: 0.34em;
  color: var(--gold);
  text-transform: uppercase;
}

.date-line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-family: var(--serif);
  font-variant-numeric: tabular-nums;
  color: #f4eee2;
}

.date-line span {
  font-size: clamp(38px, 4vw, 50px);
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.1;
}

.date-line i {
  font-style: normal;
  font-size: 22px;
  color: var(--gold-deep);
}

.date-line small {
  font-size: 26px;
  color: var(--text-dim);
}

.date-year {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  letter-spacing: 0.22em;
  color: var(--text-dim);
}

.date-year::before {
  content: "";
  width: 18px;
  height: 1px;
  background: var(--gold-deep);
}

.time-big {
  font-family: var(--serif);
  font-size: clamp(30px, 3vw, 40px);
  font-weight: 600;
  color: var(--gold-soft);
  letter-spacing: 0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.time-big em {
  font-style: normal;
  font-size: 15px;
  letter-spacing: 0.14em;
  color: var(--text-dim);
  margin-left: 4px;
}

.col-note {
  margin: 0;
  font-size: 13px;
  line-height: 1.9;
  color: var(--text-dim);
}

.col-note strong {
  color: var(--text);
  font-weight: 400;
}

.testpanel__col--download .dl-date {
  font-family: var(--serif);
  font-size: 26px;
  color: #f2ecdf;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

.notice-strip {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 18px;
  padding: 18px 26px;
  border: 1px solid rgba(168, 64, 47, 0.32);
  background: linear-gradient(90deg, rgba(168, 64, 47, 0.13), rgba(168, 64, 47, 0.02));
  font-size: 13px;
  color: #d9cbbf;
}

.notice-strip svg {
  width: 18px;
  height: 18px;
  flex: none;
  color: var(--cinnabar-soft);
}

.notice-strip b {
  font-weight: 400;
  color: #f2e4d8;
  letter-spacing: 0.06em;
}

/* ---------- activities ---------- */

.acts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 42px;
}

.act {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 34px 30px 30px;
  min-height: 440px;
  border: 1px solid var(--line-soft);
  background:
    linear-gradient(170deg, rgba(24, 29, 36, 0.92), rgba(10, 13, 17, 0.94));
  transition: transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1), border-color 0.45s ease, box-shadow 0.45s ease;
  overflow: hidden;
}

.act::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(201, 169, 97, 0.55), transparent);
  opacity: 0;
  transition: opacity 0.45s ease;
}

.act:hover {
  transform: translateY(-5px);
  border-color: rgba(201, 169, 97, 0.42);
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.5);
}

.act:hover::after {
  opacity: 1;
}

.act__ghost {
  position: absolute;
  top: 12px;
  right: 20px;
  font-family: var(--serif);
  font-size: 88px;
  line-height: 1;
  font-weight: 700;
  color: rgba(201, 169, 97, 0.07);
  pointer-events: none;
}

.act__head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 25px;
}

.act__tag {
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(201, 169, 97, 0.45);
  font-family: var(--serif);
  font-size: 14px;
  color: var(--gold);
}

.act__kind {
  font-size: 10px;
  letter-spacing: 0.3em;
  color: var(--text-faint);
  text-transform: uppercase;
}

.act h3 {
  margin: 0 0 14px;
  font-family: var(--serif);
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #f3ede1;
  line-height: 1.5;
}

.act__desc {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.95;
  color: var(--text-dim);
}

.act__desc strong {
  font-weight: 400;
  color: #e5ddcf;
}

.act__reward {
  margin-top: auto;
  padding-top: 22px;
  border-top: 1px dashed rgba(201, 169, 97, 0.24);
}

.act__reward-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  letter-spacing: 0.26em;
  color: var(--gold);
  margin-bottom: 10px;
}

.act__reward-label::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(201, 169, 97, 0.28), transparent);
}

.act__reward p {
  margin: 0;
  font-family: var(--serif);
  font-size: 16px;
  line-height: 1.75;
  color: var(--gold-soft);
  letter-spacing: 0.04em;
}

.act__reward p em {
  font-style: normal;
  color: #fdf6e9;
}

.ranks {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ranks li {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 10px;
  align-items: baseline;
  font-size: 13px;
  line-height: 1.7;
  color: var(--text-dim);
}

.ranks b {
  font-weight: 400;
  font-size: 11px;
  letter-spacing: 0.1em;
  color: var(--gold);
  border-bottom: 1px solid rgba(201, 169, 97, 0.3);
  padding-bottom: 2px;
  text-align: center;
}

.ranks li:first-child b {
  color: #f6e2b8;
  border-color: rgba(246, 226, 184, 0.6);
}

.ranks span {
  color: #ddd5c6;
}

/* ---------- tips & footer ---------- */

.divider {
  display: flex;
  align-items: center;
  gap: 18px;
  margin: 96px 0 0;
}

.divider__line {
  height: 1px;
  flex: 1;
  background: linear-gradient(90deg, transparent, rgba(201, 169, 97, 0.3), transparent);
}

.divider__mark {
  width: 7px;
  height: 7px;
  border: 1px solid var(--gold-deep);
  transform: rotate(45deg);
}

.tips {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 48px;
  padding: 58px 0 0;
}

.tips__label h3 {
  margin: 0 0 10px;
  font-family: var(--serif);
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.14em;
  color: var(--text);
}

.tips__label span {
  font-size: 10px;
  letter-spacing: 0.3em;
  color: var(--text-faint);
  text-transform: uppercase;
}

.tips__list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 36px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: tip;
}

.tips__list li {
  position: relative;
  padding-left: 30px;
  font-size: 13.5px;
  line-height: 1.95;
  color: var(--text-dim);
}

.tips__list li::before {
  counter-increment: tip;
  content: counter(tip, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 2px;
  font-family: var(--serif);
  font-size: 11px;
  letter-spacing: 0.1em;
  color: var(--gold-deep);
}

.signoff {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  margin-top: 62px;
  padding-top: 34px;
  border-top: 1px solid var(--line-soft);
}

.signoff__text {
  text-align: right;
  margin-left: auto;
}

.signoff__team {
  font-family: var(--serif);
  font-size: 17px;
  letter-spacing: 0.16em;
  color: #e6dfd1;
  margin: 0 0 6px;
}

.signoff__date {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.2em;
  color: var(--text-faint);
  font-variant-numeric: tabular-nums;
}

.signoff__seal {
  width: 54px;
  height: 54px;
  flex: none;
  display: grid;
  place-items: center;
  border: 1px solid rgba(200, 96, 74, 0.6);
  border-radius: 3px;
  color: rgba(220, 120, 98, 0.92);
  font-family: var(--serif);
  font-size: 11px;
  letter-spacing: 0.1em;
  line-height: 1.2;
  text-align: center;
  transform: rotate(-4deg);
  opacity: 0.85;
}

.pagefoot {
  position: relative;
  z-index: 2;
  margin-top: 70px;
  border-top: 1px solid rgba(201, 169, 97, 0.12);
  background: rgba(6, 8, 10, 0.6);
}

.pagefoot__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 26px 0 34px;
  font-size: 11px;
  letter-spacing: 0.16em;
  color: var(--text-faint);
}

.pagefoot__inner em {
  font-style: normal;
  color: var(--gold-deep);
}

/* ---------- reveal ---------- */

.reveal {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 0.9s cubic-bezier(0.2, 0.7, 0.2, 1), transform 0.9s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.reveal.is-in {
  opacity: 1;
  transform: none;
}

/* ---------- responsive ---------- */

@media (max-width: 1020px) {
  .section-head {
    grid-template-columns: 72px 1fr;
    gap: 20px;
  }

  .section-head__num {
    font-size: 42px;
  }

  .section-head__note {
    grid-column: 1 / -1;
    max-width: none;
    text-align: left;
    margin-top: 6px;
  }

  .tech {
    grid-template-columns: 1fr 1fr;
  }

  .tech__item--wide {
    grid-column: span 1;
  }

  .testpanel {
    grid-template-columns: 1fr 1fr;
  }

  .testpanel__col--main {
    grid-column: 1 / -1;
    border-right: 0;
    border-bottom: 1px solid var(--line-soft);
  }

  .acts {
    grid-template-columns: 1fr;
  }

  .act {
    min-height: 0;
  }

  .act__reward {
    margin-top: 26px;
  }

  .tips {
    grid-template-columns: 1fr;
    gap: 26px;
  }
}

@media (max-width: 760px) {
  .wuxia-notice {
    font-size: 15px;
  }

  .shell {
    padding: 0 22px;
  }

  .topbar__inner {
    height: 60px;
  }

  .brand__name {
    font-size: 16px;
    letter-spacing: 0.1em;
  }

  .brand__name em {
    display: none;
  }

  .topbar__tag .topbar__dot,
  .topbar__tag b {
    display: none;
  }

  .hero__inner {
    padding-top: 64px;
    padding-bottom: 84px;
  }

  .hero__frame {
    inset: 14px 12px;
  }

  .hero h1 {
    margin-top: 24px;
    letter-spacing: 0.02em;
    line-height: 1.32;
  }

  .hero__meta {
    gap: 10px 18px;
    margin-top: 28px;
  }

  .hero__meta-sep {
    display: none;
  }

  .seal {
    width: 68px;
    height: 68px;
    right: 18px;
    bottom: 26px;
  }

  .seal__grid {
    width: 48px;
    height: 48px;
    padding: 5px;
    font-size: 16px;
    gap: 2px;
  }

  .lead {
    margin-top: -40px;
  }

  .lead__panel {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 28px 24px 30px;
  }

  .lead__mark {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }

  .lead__mark span {
    font-size: 24px;
  }

  .lead__mark i {
    width: 40px;
    height: 1px;
    min-height: 0;
    flex: none;
    background: linear-gradient(90deg, var(--line), transparent);
  }

  .lead__text {
    font-size: 16px;
    line-height: 1.95;
  }

  .section {
    padding-top: 64px;
  }

  .section-head {
    grid-template-columns: 52px 1fr;
    gap: 14px;
    padding-bottom: 18px;
  }

  .section-head__num {
    font-size: 32px;
  }

  .section-head__title h2 {
    font-size: 22px;
    letter-spacing: 0.08em;
  }

  .tech {
    grid-template-columns: 1fr;
    border-left: 0;
  }

  .tech__item {
    padding: 26px 4px 26px 0;
    border-right: 0;
  }

  .tech__item h3 {
    font-size: 17px;
  }

  .testpanel {
    grid-template-columns: 1fr;
    margin-top: 28px;
  }

  .testpanel__col {
    padding: 28px 24px;
    border-right: 0;
    border-bottom: 1px solid var(--line-soft);
  }

  .testpanel__col:last-child {
    border-bottom: 0;
  }

  .date-line {
    flex-wrap: wrap;
  }

  .testpanel__col--main {
    border-bottom: 1px solid var(--line-soft);
  }

  .notice-strip {
    align-items: flex-start;
    padding: 16px 18px;
    line-height: 1.8;
  }

  .acts {
    margin-top: 28px;
    gap: 14px;
  }

  .act {
    padding: 28px 24px 26px;
  }

  .act__ghost {
    font-size: 66px;
    top: 14px;
    right: 16px;
  }

  .act h3 {
    font-size: 20px;
  }

  .tips__list {
    grid-template-columns: 1fr;
  }

  .signoff {
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 44px;
  }

  .signoff__text {
    text-align: left;
    margin-left: 0;
  }

  .divider {
    margin-top: 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .wuxia-notice,
  .wuxia-notice * {
    animation-duration: 0.001ms !important;
    transition-duration: 0.001ms !important;
  }
}
</style>
